import { View } from '@/components/Layout/View/View';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { type LucideIcon, MoreVertical } from 'lucide-react';
import React, { Suspense, memo, useEffect, useRef, useState } from 'react';

export interface PanelAction {
  icon: LucideIcon;
  label: string;
  /** Secondary label shown below main label (bold) - useful for displaying contact names */
  sublabel?: string | null;
  onCalled: () => void;
  variant?: 'default' | 'destructive';
  disabled?: boolean;
}

export interface PanelProps {
  idx: number;
  children: React.ReactNode;
  width?: 'collapsed' | 'compact' | 'wide' | 'full';
  /** Content-aware max width hint — caps the panel so the context panel absorbs extra space */
  maxPanelWidth?: 'sm' | 'md' | 'lg';
  description?: string;
  title?: string;
  icon?: LucideIcon;
  hidden?: boolean;
  actions?: PanelAction[];
  headerActions?: React.ReactNode;
}

// Type for Panel components rendered by Stack (excludes children since Panel components render their own content)
export interface RoutedPanelProps extends Omit<PanelProps, 'children'> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  params: any; // Record<string, string>
  /**
   * Params of the parent panel's path entry. Empty record at the root.
   * Used by recursive routes (e.g. the docs group panels) where the child
   * panel's own id is encoded as the parent's optional selection slot;
   * the child reads `parentParams.<slotName>` for its own id and uses
   * `params.<slotName>` for its in-panel selection. Most panels can ignore
   * this and rely on `params` alone.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  parentParams: any; // Record<string, string>
  path: string;
  basePath: string;
  // Make these required for routed panels
  idx: number;
  title: string;
  width: 'collapsed' | 'compact' | 'wide' | 'full';
  maxPanelWidth?: 'sm' | 'md' | 'lg';
}

export const MAX_WIDTH_CLASS = {
  sm: 'max-w-xl',
  md: 'max-w-3xl',
  lg: 'max-w-5xl',
} as const;

const InnerPanel: React.FC<PanelProps> = ({
  children,
  width = 'wide',
  maxPanelWidth,
  description,
  title,
  icon: Icon,
  hidden = false,
  actions,
  headerActions,
}) => {
  /* fadeIn mode on mount reduces layout width to prevent the stack from jerking during fade-in */
  useEffect(() => {
    setTimeout(() => setFadeIn(false), 300);
  }, []);
  const [fadeIn, setFadeIn] = useState(true);
  const [isStuck, setIsStuck] = useState(false);
  const sentinelRef = useRef<HTMLDivElement>(null);

  // Detect when header becomes sticky using Intersection Observer
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsStuck(!entry.isIntersecting);
      },
      { threshold: [1] },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      className={cn(
        '@container',
        'block shrink-0',
        hidden && 'hidden',
        // Sizing + scroll container (scrollbar lives here, outside the card)
        'sticky top-2',
        'md:max-h-[calc(100vh-var(--spacing)*14+2px)]',
        'md:min-h-[calc(100vh-var(--spacing)*14+2px)]',
        'md:overflow-y-auto',
        // Custom thin rounded scrollbar
        'md:[&::-webkit-scrollbar]:w-2',
        'md:[&::-webkit-scrollbar-track]:bg-transparent',
        'md:[&::-webkit-scrollbar-thumb]:bg-border',
        'md:[&::-webkit-scrollbar-thumb]:rounded-full',
        'md:[&::-webkit-scrollbar-thumb]:border-2',
        'md:[&::-webkit-scrollbar-thumb]:border-transparent',
        'md:[&::-webkit-scrollbar-thumb]:bg-clip-padding',
        'md:hover:[&::-webkit-scrollbar-thumb]:bg-muted-foreground/50',
        'md:[scrollbar-width:thin]',
        'md:[scrollbar-color:var(--border)_transparent]',
        /*
         * Content-aware panel widths:
         * - compact (context panel): growable with min-width so it absorbs
         *   remaining space when the last panel is width-capped
         * - full (last panel): grows freely unless maxPanelWidth caps it,
         *   causing remaining space to flow back to the compact panel
         */
        width == 'full' && 'grow-[6] md:w-lg',
        width == 'wide' && 'max-w-6xl grow md:w-lg',
        width == 'compact' && 'grow md:w-md md:min-w-sm',
        (width == 'full' || width == 'compact') &&
          maxPanelWidth &&
          MAX_WIDTH_CLASS[maxPanelWidth],
        // Horizontal padding so the card's shadow isn't clipped by overflow-y-auto
        // 'md:px-2 md:-mx-2',
        fadeIn && 'w-0! pl-0 md:w-0!',
        width === 'collapsed' && 'w-0! md:w-0! md:pl-0',
        'bg-card md:rounded-lg',
        'border border-transparent dark:border-white/5',
        'shadow-[0_4px_8px_rgba(0,0,0,0.2)]',
      )}
    >
      <div
        className={cn(
          'md:min-h-[calc(100vh-var(--spacing)*15)]',
          'mb-1 block shrink-0',
          width == 'wide' && 'max-w-6xl grow md:w-lg',
          width == 'compact' || (width === 'collapsed' && 'md:w-xs'),
          'transition-[width] duration-300 ease-in-out',
          width === 'collapsed' && 'origin-left scale-100 opacity-0',
        )}
      >
        {/* Sentinel element to detect sticky state */}
        {(title || Icon) && (
          <div ref={sentinelRef} className="sticky top-[-1px] h-0" />
        )}

        {/* Panel Header */}
        {(title || Icon) && (
          <div
            className={cn(
              'flex items-center justify-between border-b p-1 transition-colors md:p-2',
              // Mobile: sticky header at top of viewport with shadow for depth
              'bg-card sticky top-0 z-10 md:rounded-t-lg',
              // Desktop: header scrolls naturally with panel content (not sticky)
              // Add shadow on mobile only when stuck to separate header from content when scrolling
              isStuck ? 'shadow-sm' : 'shadow-none',
              // isSearchTarget ? 'bg-primary/5 border-primary/20' : 'bg-muted/30',
            )}
          >
            <div className="m-2 flex min-w-0 items-center gap-2">
              {Icon && (
                <Icon className="text-muted-foreground h-4 w-4 shrink-0" />
              )}
              {title && (
                <h3 className="truncate text-sm leading-none font-medium">
                  {title}
                </h3>
              )}
            </div>
            <div className="flex items-center gap-2">
              {headerActions}
              {actions && actions.length > 0 && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8">
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {actions.map((action, idx) => {
                      const ActionIcon = action.icon;
                      return (
                        <DropdownMenuItem
                          key={idx}
                          onClick={action.onCalled}
                          variant={action.variant}
                          disabled={action.disabled}
                        >
                          <ActionIcon className="mr-2 h-4 w-4 flex-shrink-0" />
                          <span className="flex flex-col items-start">
                            <span>{action.label}</span>
                            {action.sublabel && (
                              <span className="text-muted-foreground italic">
                                {action.sublabel}
                              </span>
                            )}
                          </span>
                        </DropdownMenuItem>
                      );
                    })}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </div>
        )}

        {/* Panel Content */}
        <div
          className={cn(
            'p-2 md:p-4',
            'pb-20 md:pb-4', // Bottom clearance for mobile menu FAB
          )}
        >
          <Suspense fallback="loading...">
            {description && <div>Description: {description}</div>}
            <View title={title || ''}>
              <div>{children}</div>
            </View>
          </Suspense>
        </div>
      </div>
    </section>
  );
};
export const Panel = memo(InnerPanel) as typeof InnerPanel;
